import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Upload,
  Save,
  ArrowLeft,
  Loader2,
  Trash2,
  Image as ImageIcon,
} from "lucide-react";
import {
  AddTestimonialsThunk,
  GetSingleTestimonialsThunk,
  UpdateTestimonialsThunk,
} from "../../redux/states/testimonial/thunk";

const CreateUpdateTestimonials = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();

  const { singleTestimonial, loading, error } = useSelector(
    (state) => state.testimonials
  );

  const [formData, setFormData] = useState({
    name: "",
    title: "",
    designation: "",
    company_name: "",
    message: "",
    rating: "",
    status: "active",
    image: null,
  });

  const [preview, setPreview] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // ✅ Load testimonial for edit
  useEffect(() => {
    if (uuid) {
      setIsUpdating(true);
      dispatch(GetSingleTestimonialsThunk(uuid));
    }
  }, [uuid, dispatch]);

  // ✅ Prefill form when fetched
  useEffect(() => {
    if (singleTestimonial && isUpdating) {
      setFormData({
        name: singleTestimonial.name || "",
        title: singleTestimonial.title || "",
        designation: singleTestimonial.designation || "",
        company_name: singleTestimonial.company_name || "",
        message: singleTestimonial.message || "",
        rating: singleTestimonial.rating || "",
        status: singleTestimonial.status || "active",
        image: null,
      });

      if (singleTestimonial.image) {
        setPreview(
          `${process.env.REACT_APP_API_URL}/${singleTestimonial.image}`
        );
      }
    }
  }, [singleTestimonial, isUpdating]);

  // ✅ Input change handler
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      setFormData({ ...formData, [name]: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // ✅ Remove image
  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    setPreview(null);
  };

  // ✅ Submit handler
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Please enter name");
      return;
    }

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) data.append(key, value);
    });
    if (isUpdating) data.append("uuid", uuid);

    if (isUpdating) {
      dispatch(UpdateTestimonialsThunk({ uuid, formData: data })).then(() => {
        navigate("/testimonials");
      });
    } else {
      dispatch(AddTestimonialsThunk(data)).then(() => {
        navigate("/testimonials");
      });
    }
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-12xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border p-6 mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-small font-bold text-gray-800">
                {isUpdating ? "Update Testimonial" : "Add New Testimonial"}
              </h1>
              <p className="text-gray-600 mt-1">
                {isUpdating
                  ? "Edit testimonial details below."
                  : "Fill the form to create a new testimonial."}
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 border border-gray-300 px-4 py-2 rounded-lg transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Details */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Testimonial Details
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  ["name", "Name *"],
                  ["title", "Title"],
                  ["designation", "Designation"],
                  ["company_name", "Company Name"],
                  ["message", "Message"],
                  ["rating", "Rating (1-5)"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-gray-700 font-medium mb-2">
                      {label}
                    </label>
                    <input
                      type={key === "rating" ? "number" : "text"}
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                      min={key === "rating" ? 1 : undefined}
                      max={key === "rating" ? 5 : undefined}
                    />
                  </div>
                ))}

                {/* Status */}
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Testimonial Image
              </h2>

              <div className="flex flex-col items-center space-y-4">
                {preview ? (
                  <div className="relative">
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-48 h-48 object-cover rounded-xl border shadow"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-48 h-48 flex flex-col items-center justify-center border-2 border-dashed rounded-xl text-gray-400">
                    <ImageIcon className="w-10 h-10 mb-2" />
                    No image selected
                  </div>
                )}

                <label className="flex items-center gap-2 bg-blue-600 text-white px-5 py-3 rounded-xl cursor-pointer hover:bg-blue-700 transition">
                  <Upload className="w-5 h-5" />
                  {preview ? "Change Image" : "Upload Image"}
                  <input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Submit */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isUpdating ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {isUpdating ? "Update Testimonial" : "Save Testimonial"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default CreateUpdateTestimonials;
